import { useState, useEffect } from 'react';
import { DemoVideoEntry, DemoVideoCreatePayload, DemoVideoUpdatePayload, CompanyFieldOverridePayload } from '../types/override.types';
import { companyOverrideService } from '../services/company-override.service';

export const useAdminOverride = (companyId: number) => {
  const [videos, setVideos] = useState<DemoVideoEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVideos = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await companyOverrideService.listDemoVideos(companyId);
      setVideos(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load demo videos');
    } finally {
      setIsLoading(false);
    }
  };

  const createVideo = async (payload: DemoVideoCreatePayload) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.createDemoVideo(companyId, payload);
      await loadVideos();
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateVideo = async (videoId: string, payload: DemoVideoUpdatePayload) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.updateDemoVideo(companyId, videoId, payload);
      await loadVideos();
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteVideo = async (videoId: string) => {
    setIsSubmitting(true);
    try {
      await companyOverrideService.deleteDemoVideo(companyId, videoId);
      await loadVideos();
    } finally {
      setIsSubmitting(false);
    }
  };

  const overrideFields = async (payload: CompanyFieldOverridePayload) => {
    setIsSubmitting(true);
    try {
      const result = await companyOverrideService.overrideCompanyFields(companyId, payload);
      return result;
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    loadVideos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return {
    videos,
    isLoading,
    isSubmitting,
    error,
    loadVideos,
    createVideo,
    updateVideo,
    deleteVideo,
    overrideFields,
  };
};
